import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Wrench } from "lucide-react";

export default function RegisterPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="register-page" className="min-h-[calc(100vh-4rem)] grid grid-cols-1 lg:grid-cols-2">
      <div className="bg-zinc-950/40 border-r border-zinc-900 p-10 lg:p-16 flex flex-col justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="h-9 w-9 bg-brand-red grid place-items-center"><Wrench className="h-5 w-5 text-black" strokeWidth={2.5} /></div>
          <div className="font-display uppercase text-xl text-white tracking-tight">Tassie Automotive</div>
        </Link>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-brand-red">New customer</div>
          <h1 className="mt-4 font-display uppercase text-5xl lg:text-6xl tracking-tighter text-white leading-[0.9]">
            One account.<br />
            <span className="text-brand-red">Every service.</span>
          </h1>
          <p className="mt-6 text-zinc-400 max-w-md">
            Track repairs, store vehicle details, and book your next service in less than a minute.
          </p>
        </div>
        <p className="font-mono text-xs uppercase tracking-widest text-zinc-600">Bay 06 — Alignment online</p>
      </div>

      <div className="p-10 lg:p-16 flex items-center">
        <form data-testid="register-form" onSubmit={submit} className="w-full max-w-md space-y-5">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-brand-red">Sign up</div>
            <h2 className="mt-2 font-display uppercase text-3xl text-white tracking-tight">Create your account</h2>
          </div>

          <Field label="Full name">
            <input data-testid="reg-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="ta-input" placeholder="Jane Citizen" />
          </Field>
          <Field label="Email">
            <input data-testid="reg-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="ta-input" placeholder="you@example.com" />
          </Field>
          <Field label="Phone (optional)">
            <input data-testid="reg-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="ta-input" placeholder="0412 345 678" />
          </Field>
          <Field label="Password (min 6 characters)">
            <input data-testid="reg-password" required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="ta-input" placeholder="••••••••" />
          </Field>

          {error && <div data-testid="register-error" className="border border-red-900 bg-red-950/40 text-red-300 text-sm px-4 py-3">{error}</div>}

          <button data-testid="register-submit" disabled={loading} className="w-full px-7 py-4 bg-brand-red text-white font-bold uppercase tracking-wider hover:bg-[#B91C2B] disabled:opacity-60">
            {loading ? "Creating account…" : "Create account"}
          </button>

          <p className="text-sm text-zinc-400">
            Already registered?{" "}
            <Link data-testid="register-to-login" to="/login" className="text-brand-red hover:underline">Sign in</Link>
          </p>
        </form>
      </div>

      <style>{`
        .ta-input { width:100%; background:#0a0a0a; border:1px solid #27272a; color:#fafafa; padding:12px 14px; font-family:'IBM Plex Sans',sans-serif; font-size:14px; outline:none; transition:border-color .15s; }
        .ta-input:focus { border-color:#E11D2B; box-shadow:0 0 0 1px #E11D2B; }
        .ta-input::placeholder { color:#52525b; }
      `}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);
